import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Calendar, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export const MyBookings = () => {
  const { token } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [roomTypes, setRoomTypes] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      const [bookingsData, roomTypesData] = await Promise.all([
        api.get('/api/bookings/my-bookings', token),
        api.get('/api/room-types'),
      ]);
      
      setBookings(bookingsData);
      
      const roomTypesMap = {};
      roomTypesData.forEach(rt => {
        roomTypesMap[rt.id] = rt;
      });
      setRoomTypes(roomTypesMap);
    } catch (error) {
      toast.error('Erro ao carregar reservas');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (bookingId) => {
    if (!window.confirm('Tem certeza que deseja cancelar esta reserva?')) {
      return;
    }

    try {
      await api.post(`/api/bookings/${bookingId}/cancel`, {}, token);
      toast.success('Reserva cancelada com sucesso');
      loadBookings();
    } catch (error) {
      toast.error(error.message || 'Erro ao cancelar reserva');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending_payment: { label: 'Pagamento Pendente', className: 'bg-yellow-100 text-yellow-800' },
      confirmed: { label: 'Confirmada', className: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Cancelada', className: 'bg-red-100 text-red-800' },
      refunded: { label: 'Reembolsada', className: 'bg-blue-100 text-blue-800' },
    };
    const statusInfo = statusMap[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusInfo.className}`}>
        {statusInfo.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12" data-testid="my-bookings-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-display font-bold text-primary mb-8">
          Minhas Reservas
        </h1>

        {bookings.length === 0 ? (
          <div className="bg-white p-12 rounded-xl shadow-lg text-center">
            <p className="text-text-secondary mb-4">Você ainda não possui reservas.</p>
            <Button onClick={() => window.location.href = '/rooms'} data-testid="go-to-rooms-button">
              Ver Quartos Disponíveis
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {bookings.map((booking) => {
              const roomType = roomTypes[booking.room_type_id];
              return (
                <div
                  key={booking.id}
                  className="bg-white p-6 rounded-xl shadow-lg"
                  data-testid={`booking-card-${booking.id}`}
                >
                  <div className="flex flex-col md:flex-row gap-6">
                    {roomType && roomType.images && roomType.images[0] && (
                      <img
                        src={roomType.images[0]}
                        alt={roomType.name}
                        className="w-full md:w-48 h-32 object-cover rounded-lg"
                      />
                    )}
                    
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-xl font-display font-semibold text-primary">
                            {roomType ? roomType.name : 'Quarto'}
                          </h3>
                          <p className="text-sm text-text-muted">ID: {booking.id.slice(0, 8)}</p>
                        </div>
                        {getStatusBadge(booking.status)}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                        <div className="flex items-center gap-2 text-text-secondary">
                          <Calendar className="h-4 w-4" />
                          <span className="text-sm">
                            Check-in: {format(new Date(booking.check_in), 'dd/MM/yyyy')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-text-secondary">
                          <Calendar className="h-4 w-4" />
                          <span className="text-sm">
                            Check-out: {format(new Date(booking.check_out), 'dd/MM/yyyy')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-text-secondary">
                          <Users className="h-4 w-4" />
                          <span className="text-sm">{booking.guests} hóspede(s)</span>
                        </div>
                        <div className="text-sm text-text-secondary">
                          Total: <span className="font-bold text-accent">R$ {booking.total_price.toFixed(2)}</span>
                        </div>
                      </div>

                      {booking.status === 'confirmed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleCancel(booking.id)}
                          data-testid={`cancel-booking-${booking.id}`}
                        >
                          <X className="h-4 w-4 mr-2" />
                          Cancelar Reserva
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
